import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import {
  BUOY_NO_TEMPERATURE_COLOR,
  BUOY_TEMPERATURE_DOMAIN,
  buoyTemperatureGradient,
} from '../../../Buoy/map/buoyTemperatureLegendUtils';
import USGSMarker from './USGSMarker';

type USGSMarkerState =
  | 'majorFlood'
  | 'moderateFlood'
  | 'minorFlood'
  | 'actionStage'
  | 'notFlooding'
  | 'noFloodStages'
  | 'measurement'
  | 'unavailable'
  | string;

type USGSMarkerType = 'lake' | 'stream' | 'wetland' | 'estuary' | 'coastal';

interface DeviceMapLegendProps {
  className?: string;
  compact?: boolean;
  defaultBuoyOpen?: boolean;
  defaultUSGSOpen?: boolean;
  setLegendToggle?: React.Dispatch<React.SetStateAction<boolean>>;
  showBuoySection?: boolean;
  showUSGSSection?: boolean;
  style?: React.CSSProperties;
}

const DEFAULT_STATES: USGSMarkerState[] = [
  'major',
  'moderate',
  'minor',
  'action',
  'notFlooding',
  'noFloodStages',
  'measurement',
  'unavailable',
];

const DEFAULT_TYPES: USGSMarkerType[] = ['lake', 'stream', 'wetland', 'estuary', 'coastal'];

const STATE_LABELS: Record<string, string> = {
  major: 'In major flood stage',
  moderate: 'In moderate flood stage',
  minor: 'In minor flood stage',
  action: 'In action stage',
  notFlooding: 'Not flooding',
  noFloodStages: 'Flood stages not established',
  measurement: 'Measurement flag',
  unavailable: 'Recent measurement unavailable',
};

type LegendSectionKey = 'usgs' | 'buoy';

function formatLabel(value: string) {
  return value
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (char) => char.toUpperCase())
    .trim();
}

function formatStateLabel(state: string) {
  return STATE_LABELS[state] || formatLabel(state);
}

function BuoyLegendSection() {
  return (
    <div className="d-flex flex-column gap-2">
      <div className="text-white-50" style={{ fontSize: '0.85rem', lineHeight: 1.35 }}>
        Marker color reflects reported buoy temperature.
        <br />
        air temperature is used when available, otherwise water temperature will be used.
      </div>
      <div
        className="w-100 rounded-2 border border-light border-opacity-25"
        style={{
          height: '0.8rem',
          background: buoyTemperatureGradient,
        }}
      />
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-1">
        {BUOY_TEMPERATURE_DOMAIN.map((temp) => (
          <span key={temp}>{temp}</span>
        ))}
      </div>
      <div className="d-flex align-items-center gap-2">
        <span
          className="rounded-circle d-inline-block border border-light border-opacity-50"
          style={{
            width: 11,
            height: 11,
            backgroundColor: BUOY_NO_TEMPERATURE_COLOR,
            flexShrink: 0,
          }}
        />
        <span>No reported temperature</span>
      </div>
    </div>
  );
}

function USGSLegendSection() {
  return (
    <div className="d-flex flex-column gap-2">
      <div>
        <div className="text-uppercase opacity-75 mb-1" style={{ fontSize: '0.72rem' }}>
          Color - Flood Condition
        </div>
        <div className="d-flex flex-column gap-1">
          {DEFAULT_STATES.map((state) => (
            <div key={state} className="d-flex align-items-center gap-2">
              <div
                className="d-flex align-items-center justify-content-center"
                style={{ width: 13, height: 13 }}
              >
                <USGSMarker type="stream" state={state} width={13} height={13} className="" />
              </div>
              <div>{formatStateLabel(state)}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="text-uppercase opacity-75 mb-1" style={{ fontSize: '0.72rem' }}>
          Shape - Site Type
        </div>
        <div className="row row-cols-2 g-1">
          {DEFAULT_TYPES.map((type) => (
            <div key={type} className="col">
              <div className="d-flex align-items-center gap-2">
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ width: 13, height: 13 }}
                >
                  <USGSMarker type={type} state="unavailable" width={13} height={13} className="" />
                </div>
                <div>{formatLabel(type)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function DeviceMapLegend({
  className = '',
  compact = false,
  defaultBuoyOpen = true,
  defaultUSGSOpen = true,
  setLegendToggle,
  showBuoySection = false,
  showUSGSSection = true,
  style,
}: DeviceMapLegendProps) {
  const availableSections = useMemo(() => {
    const sections: Array<{ key: LegendSectionKey; label: string; title: string }> = [];

    if (showUSGSSection) {
      sections.push({
        key: 'usgs',
        label: 'USGS Monitoring',
        title: 'USGS Monitoring Stations',
      });
    }

    if (showBuoySection) {
      sections.push({
        key: 'buoy',
        label: 'Buoy Temperature',
        title: 'Buoy Temperature Scale',
      });
    }

    return sections;
  }, [showBuoySection, showUSGSSection]);

  const [activeSection, setActiveSection] = useState<LegendSectionKey>(() => {
    if (showUSGSSection && defaultUSGSOpen) return 'usgs';
    if (showBuoySection && defaultBuoyOpen) return 'buoy';
    if (showUSGSSection) return 'usgs';
    return 'buoy';
  });

  useEffect(() => {
    if (availableSections.some((section) => section.key === activeSection)) return;
    if (availableSections[0]) {
      setActiveSection(availableSections[0].key);
    }
  }, [activeSection, availableSections]);

  const activeSectionMeta =
    availableSections.find((section) => section.key === activeSection) || availableSections[0];

  const legendSectionOptions = availableSections.map((section) => ({
    value: section.key,
    label: section.label,
  }));

  const renderActiveSection = () => {
    if (activeSection === 'buoy') {
      return <BuoyLegendSection />;
    }

    return <USGSLegendSection />;
  };

  return (
    <div
      className={`connected-devices-map-legend bg-dark bg-opacity-75 text-white rounded-3 shadow-lg max-w-3xs d-flex flex-column min-h-0 ${compact ? 'p-2' : 'p-2'} ${className}`}
      style={{
        width: '100%',
        maxHeight: '100%',
        overflow: 'hidden',
        position: 'relative',
        ...style,
      }}
      role="dialog"
      aria-label="Map legend"
    >
      <Button
        type="button"
        variant="outline-warning"
        size="sm"
        className="position-absolute top-0 end-0 m-2 rounded-circle"
        aria-label="Close"
        onClick={() => setLegendToggle?.(false)}
        style={{ width: 26, height: 26, padding: 0, lineHeight: 1 }}
      >
        X
      </Button>

      <div className="pe-4 mb-2">
        <div className="fw-semibold" style={{ fontSize: compact ? '0.88rem' : '0.95rem' }}>
          Map Legend
        </div>
      </div>

      <div
        className="d-flex flex-column gap-2 pe-1 flex-grow-1 min-h-0"
        style={{
          overflow: 'hidden',
        }}
      >
        {availableSections.length > 1 && (
          <BaseSelect
            wrapperClassName="flex-shrink-0"
            isSearchable={false}
            // The legend sits on a dark map panel; keep the dark treatment.
            classNames={{
              control: () => 'bg-dark text-white border-light border-opacity-25',
              menu: () => 'bg-dark text-white border-light border-opacity-25',
              option: ({ isFocused, isSelected }) =>
                isSelected || isFocused ? 'bg-secondary text-white' : 'bg-dark text-white',
              singleValue: () => 'text-white',
            }}
            selectProps={{
              'aria-label': 'Legend section',
              options: legendSectionOptions,
              value: findSelectOption(legendSectionOptions, activeSection),
              onChange: (option: { value: LegendSectionKey } | null) => {
                if (option) setActiveSection(option.value);
              },
            }}
          />
        )}

        {activeSectionMeta && (
          <div className="border border-light border-opacity-25 rounded-2 overflow-hidden d-flex flex-column min-h-0 flex-grow-1">
            <div
              className="px-2 pb-2 pt-1 small min-h-0 flex-grow-1"
              style={{
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {renderActiveSection()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
